import { isExtractionTransferReady } from './recognized-stage'
import type { ExtractionField, ExtractionStage } from '../../../api/listeners/document-extraction'
import { normalizeExtractedBirthDate } from './recognized-birthdate'

export type ListenerPassport = { passport: string; sex: string }
export type ListenerSex = 'Мужской' | 'Женский'
export type RecognizedPassport = Partial<{ passport: string; sex: ListenerSex }>

const whitespace = (value: string) => value.normalize('NFC').trim().replace(/\s+/gu, ' ')
const unconfirmedLinks = new Set(['person_link_unconfirmed', 'document_link_unconfirmed'])
const passportFields = ['series', 'number', 'issuedBy', 'issueDate'] as const
const samePassport = (left: string | undefined, right: string | undefined) => left?.toLocaleLowerCase('ru') === right?.toLocaleLowerCase('ru')

export const normalizePassportIdentifier = (value: string, series = false): string | null => {
    const text = whitespace(value)
    if (!/^[\p{L}\p{N}][\p{L}\p{N} /-]*$/u.test(text)) return null
    const digits = text.replace(/ /g, '')
    if (/^\d+$/.test(digits)) return series && digits.length === 4 ? `${digits.slice(0, 2)} ${digits.slice(2)}` : digits
    return text.toUpperCase()
}

export const normalizePassportIssuer = (value: string): string | null => {
    const text = whitespace(value)
    const hasControl = [...text].some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
    return text && /\p{L}/u.test(text) && !hasControl && !/[<>]/u.test(text) ? text : null
}

export const normalizePassportSex = (value: string): ListenerSex | null => {
    const text = whitespace(value).toLocaleLowerCase('ru').replace(/\.$/, '')
    if (['м', 'муж', 'мужской', 'm', 'male'].includes(text)) return 'Мужской'
    if (['ж', 'жен', 'женский', 'f', 'female'].includes(text)) return 'Женский'
    return null
}

const readField = (field: ExtractionField | undefined, normalize: (value: string) => string | null) => {
    if (!field || ['not_present', 'not_applicable'].includes(field.status)) return { value: null, needsReview: false }
    const rawText = field.rawValue?.trim() || ''
    const normalizedText = field.normalizedValue?.trim() || ''
    const raw = rawText ? normalize(rawText) : null
    const normalized = normalizedText ? normalize(normalizedText) : null
    const value = normalized || raw
    if (!value || field.status !== 'extracted' || !field.evidence.length || field.candidates.length
        || ['failed', 'invalid', 'conflict'].includes(field.validation.format)
        || ['failed', 'invalid', 'conflict'].includes(field.validation.crossDocument)
        || (rawText && !raw) || (normalizedText && !normalized)
        || (raw && normalized && raw.toLocaleLowerCase('ru') !== normalized.toLocaleLowerCase('ru'))) return { value: null, needsReview: true }
    return { value, needsReview: false }
}

export const recognizedPassportPackets = (stage: ExtractionStage | null): { packets: RecognizedPassport[]; needsReview: boolean } => {
    let packets: RecognizedPassport[] = []
    let needsReview = false
    const readings: Record<string, string | null>[] = []
    if (!stage || !isExtractionTransferReady(stage)) return { packets, needsReview }
    for (const part of stage.parts) {
        if (part.status !== 'succeeded' || !part.result) continue
        for (const document of part.result.documents) {
            if (document.type !== 'passport_main') continue
            let documentNeedsReview = false
            const read = (name: string, normalize: (value: string) => string | null) => {
                const value = readField(document.fields.find(field => field.name === name), normalize)
                const conflict = part.result!.conflicts.some(item => !unconfirmedLinks.has(item.code)
                    && item.fields.some(ref => ref.documentId === document.documentId && ref.field === name))
                    || part.result!.unresolved.some(item => !unconfirmedLinks.has(item.code) && item.documentId === document.documentId && item.field === name)
                if (value.needsReview || conflict) documentNeedsReview = true
                return conflict ? null : value.value
            }
            const series = read('series', value => normalizePassportIdentifier(value, true))
            const number = read('number', normalizePassportIdentifier)
            const issuer = read('issuedBy', normalizePassportIssuer)
            const date = read('issueDate', normalizeExtractedBirthDate)
            const sex = read('sexAsPrinted', normalizePassportSex) as ListenerSex | null
            if (documentNeedsReview) needsReview = true
            readings.push({ series, number, issuedBy: issuer, issueDate: date, sexAsPrinted: sex })
            const packet: RecognizedPassport = {}
            // All four requisites must belong to this one passport. An incomplete
            // page never replaces an existing passport with a fragment.
            if (series && number && issuer && date) packet.passport = `${series} ${number}, выдан ${issuer}, дата выдачи ${date} г.`
            if (sex) packet.sex = sex
            if (!Object.keys(packet).length && document.fields.some(field => passportFields.includes(field.name as typeof passportFields[number]) && field.rawValue?.trim())) needsReview = true
            if (Object.keys(packet).length && !packets.some(item => samePassport(item.passport, packet.passport) && item.sex === packet.sex)) packets.push(packet)
        }
    }
    // Even an incomplete second passport can contradict a complete one. Keep
    // those readable requisites in the comparison, without joining fragments.
    for (const name of [...passportFields, 'sexAsPrinted']) {
        const values = readings.map(reading => reading[name]?.toLocaleLowerCase('ru')).filter(Boolean)
        if (new Set(values).size > 1) needsReview = true
    }
    const complete = packets.find(candidate => packets.every(packet => (!packet.passport || samePassport(packet.passport, candidate.passport))
        && (!packet.sex || packet.sex === candidate.sex)))
    if (complete) packets = [complete]
    return { packets, needsReview }
}
